/**
 * Session Event Bus
 *
 * Tiny module-level pub/sub that lets the HTTP layer signal "the user's
 * session is gone" to the React tree without importing React Router or the
 * AuthContext (which would create circular deps).
 *
 * Wiring contract:
 *  - The HTTP client / ApiService calls `emitSessionExpired()` exactly once
 *    when a 401 is received AND the refresh-token attempt also fails.
 *  - A single React component (mounted inside `AppRouter` + AuthProvider +
 *    NotificationProvider) registers a listener at mount via
 *    `subscribeToSessionExpired(cb)` and uses the callback to:
 *      1. Show a toast (`errors:token_expired`)
 *      2. Navigate to `/login`
 *      3. Clear AuthContext state (delegated to `logout()` is too heavy
 *         because storage is already wiped — we just dispatch the local
 *         LOGOUT reducer action).
 *
 * The bus is intentionally minimal: a single event, multiple subscribers,
 * fire-and-forget. We coalesce rapid-fire emits (concurrent 401s) by tracking
 * the last emit timestamp and dropping repeats within a short window — keeps
 * the toast from stacking N copies when many requests 401 in parallel.
 */

/** `reason` is set when the ACCOUNT was refused (see account-refusal.ts). */
export type SessionExpiredListener = (reason?: string) => void

const COALESCE_WINDOW_MS = 1000

const listenersRegistry = new Set<SessionExpiredListener>()
let lastEmittedAtMillis = 0

/**
 * Register a listener for the "session expired" event. Returns an unsubscribe
 * function — callers (typically a React `useEffect`) MUST call it on unmount
 * to avoid leaking listeners across re-renders / hot reloads.
 */
export function subscribeToSessionExpired(listener: SessionExpiredListener): () => void {
  listenersRegistry.add(listener)
  return () => {
    listenersRegistry.delete(listener)
  }
}

/**
 * Emit "session expired". Idempotent within `COALESCE_WINDOW_MS` so that 5
 * concurrent 401s collapse into a single toast / single navigation.
 */
export function emitSessionExpired(reason?: string): void {
  const now = Date.now()
  if (now - lastEmittedAtMillis < COALESCE_WINDOW_MS) {
    return
  }
  lastEmittedAtMillis = now
  for (const listener of listenersRegistry) {
    try {
      listener(reason)
    } catch {
      // Listeners must be self-defending; swallow to keep other subscribers
      // firing and to avoid breaking the HTTP error path.
    }
  }
}

/**
 * TEST-ONLY: reset the coalescing window and clear all registered listeners.
 * Production code MUST NOT call this — it's exported so unit tests can run
 * in isolation without bleeding listener state across cases.
 */
export function resetSessionEventsForTesting(): void {
  listenersRegistry.clear()
  lastEmittedAtMillis = 0
}
